import { Fragment, memo, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "wouter";
import { ArrowDown, ArrowLeft, ArrowRight } from "lucide-react";
import { getGetStorefrontHomepageHeroQueryKey, getGetStorefrontHomepageSettingsQueryKey, getListBrandCatalogProductsQueryKey, getListEasternProductsQueryKey, getListPublicCatalogBrandsQueryKey, getListSaleCatalogProductsQueryKey, listBrandCatalogProducts, listEasternProducts, useGetStorefrontHomepageHero, useGetStorefrontHomepageSettings, useListBrandCatalogProducts, useListEasternProducts, useListPublicCatalogBrands, useListSaleCatalogProducts } from "@workspace/api-client-react";
import type { HomepageHeroSlide, HomepageSection, ListEasternProductsCategory, PublicCatalogBrand, StorefrontHomepageHeroResponse, StorefrontSettings } from "@workspace/api-client-react";
import { ProductCard, ProductSkeletons } from "@/components/ProductCard";
import { Footer } from "@/components/StoreShell";
import { useCatalog } from "@/hooks/useCatalog";
import { useStore } from "@/components/StoreContext";
import {
  BRAND_FEATURE_IMAGE_WIDTHS,
  HERO_IMAGE_WIDTHS,
  responsiveShopifyImageSources,
} from "@/lib/responsive-images";
import { INITIAL_FEATURED_BRAND_LOGOS } from "@/lib/featured-brand-logos";
import { EASTERN_EDIT, slugify, type StoreProduct } from "@/lib/store";
import { getProductPricing } from "@/lib/estimated-pricing";
import { categoryHref } from "@/lib/categories";
import { prioritizeHomepageProducts } from "@/lib/homepage-product-selection";
import { DEFAULT_HOW_IT_WORKS_STEPS, isHowItWorksSection, parseHowItWorksSteps } from "@/lib/homepage-presentation";
import { resolveHomepageCopy, sectionCopyIsHidden } from "@/lib/homepage-copy";
import { getHomepageSectionBrands, getHomepageShopByBrandBrands, getHomepageVisibleBrands } from "@/lib/homepage-brands";

function resolvedSectionCopy(
  section: HomepageSection,
  field: "eyebrow" | "heading" | "subheading" | "ctaText",
  value: string,
  fallback: string,
  brandName = "",
) {
  return resolveHomepageCopy(value, fallback, sectionCopyIsHidden(section, field), brandName);
}

function getImageFocalPosition(
  imageUrl: string | null | undefined,
  imageFocalPoints: StorefrontSettings["imageFocalPoints"],
) {
  const focalPoint = imageUrl
    ? imageFocalPoints?.find(item => item.imageUrl === imageUrl)
    : undefined;
  return {
    xPercent: focalPoint?.xPercent ?? 50,
    yPercent: focalPoint?.yPercent ?? 15,
  };
}

function readHomepageHeroBootstrap(): StorefrontHomepageHeroResponse | undefined {
  if (typeof document === "undefined") return undefined;
  const node = document.getElementById("chamakpatti-homepage-hero");
  if (!node?.textContent) return undefined;
  try {
    const value = JSON.parse(node.textContent) as StorefrontHomepageHeroResponse;
    return typeof value.enabled === "boolean"
      && Array.isArray(value.slides)
      && typeof value.intervalSeconds === "number"
      ? value
      : undefined;
  } catch {
    return undefined;
  }
}

const EMPTY_HERO_SLIDES: HomepageHeroSlide[] = [];

export default function Home() {
  const { storefrontSettings } = useStore();
  const sections = useMemo(() => storefrontSettings.homepageSections?.length
    ? [...storefrontSettings.homepageSections].filter(section => section.enabled)
    : [], [storefrontSettings.homepageSections]);
  const hasConfiguredSections = Boolean(storefrontSettings.homepageSections?.length);
  const usesConfiguredSections = hasConfiguredSections && sections.length > 0;
  const saleEnabled = storefrontSettings.saleEnabled;
  const saleCatalog = useCatalog({
    saleOnly: true,
    sort: "featured",
    enabled: saleEnabled && !usesConfiguredSections,
  });
  const easternParams = { page: 1, sort: "featured" as const };
  const [easternSyncTimedOut, setEasternSyncTimedOut] = useState(false);
  const easternSyncStartedAt = useRef<number | null>(null);
  const easternCatalog = useListEasternProducts(easternParams, {
    query: {
      queryKey: getListEasternProductsQueryKey(easternParams),
      staleTime: 15_000,
      refetchInterval: query => query.state.data?.syncing && !query.state.data.total && !easternSyncTimedOut
        ? 30_000
        : false,
      refetchIntervalInBackground: false,
    },
  });
  const products = easternCatalog.data?.products ?? [];
  const productTotal = easternCatalog.data?.total ?? 0;
  const waitingForEasternSync = Boolean(easternCatalog.data?.syncing && productTotal === 0);
  useEffect(() => {
    if (!waitingForEasternSync) {
      easternSyncStartedAt.current = null;
      setEasternSyncTimedOut(false);
      return;
    }
    if (easternSyncTimedOut) return;
    const startedAt = easternSyncStartedAt.current ?? Date.now();
    easternSyncStartedAt.current = startedAt;
    const remaining = 30_000 - (Date.now() - startedAt);
    if (remaining <= 0) {
      setEasternSyncTimedOut(true);
      return;
    }
    const timer = window.setTimeout(() => setEasternSyncTimedOut(true), remaining);
    return () => window.clearTimeout(timer);
  }, [easternSyncTimedOut, waitingForEasternSync]);
  const loading = easternCatalog.isLoading || (waitingForEasternSync && !easternSyncTimedOut);
  const error = easternCatalog.error
    ? "The Eastern collection could not be loaded."
    : easternSyncTimedOut
      ? "The Eastern collection is still syncing. Please try again shortly."
      : !productTotal && !easternCatalog.data?.syncing && easternCatalog.data?.syncError
        ? "The Eastern collection could not be loaded."
        : "";
  const [initialHomepageHero] = useState(readHomepageHeroBootstrap);
  const homepageHero = useGetStorefrontHomepageHero({
    query: {
      queryKey: getGetStorefrontHomepageHeroQueryKey(),
      initialData: initialHomepageHero,
      staleTime: 15_000,
    },
  });
  const validHeroSlides = useMemo(() => (homepageHero.data?.slides ?? []).filter(slide =>
    Boolean(slide.brandSlug && slide.brandName && slide.brandHref && slide.productTitle && slide.imageUrl),
  ), [homepageHero.data?.slides]);
  const isHeroRotating = homepageHero.data?.enabled === true && validHeroSlides.length >= 2;
  const [heroSlideIndex, setHeroSlideIndex] = useState(0);
  const [heroRotationPaused, setHeroRotationPaused] = useState(false);
  useEffect(() => {
    if (!isHeroRotating || heroRotationPaused) return;
    const timer = window.setInterval(() => {
      setHeroSlideIndex(index => (index + 1) % validHeroSlides.length);
    }, Math.max(3, Math.min(30, homepageHero.data?.intervalSeconds ?? 6)) * 1000);
    return () => window.clearInterval(timer);
  }, [homepageHero.data?.intervalSeconds, heroRotationPaused, isHeroRotating, validHeroSlides.length]);
  const activeHeroSlide = isHeroRotating ? validHeroSlides[heroSlideIndex % validHeroSlides.length] : undefined;
  const brandQuery = useListPublicCatalogBrands({ query: { queryKey: getListPublicCatalogBrandsQueryKey() } });
  const homepageSettings = useGetStorefrontHomepageSettings({ query: { queryKey: getGetStorefrontHomepageSettingsQueryKey() } });
  const brandOrder = homepageSettings.data?.brandOrder ?? storefrontSettings.brandOrder ?? [];
  const homepageSectionBrands = useMemo(
    () => getHomepageSectionBrands(brandQuery.data?.brands ?? [], productTotal),
    [brandQuery.data?.brands, productTotal],
  );
  const sortedHomepageBrands = useMemo(
    () => applySavedBrandOrder(homepageSectionBrands, brandOrder),
    [homepageSectionBrands, brandOrder],
  );
  const shopByBrandCatalog = useMemo(() => getHomepageShopByBrandBrands(sortedHomepageBrands), [sortedHomepageBrands]);
  const homepageBrands = useMemo(() => getHomepageVisibleBrands(homepageSectionBrands), [homepageSectionBrands]);
  const brandFeatureSections = useMemo(() => sections.filter(section => section.kind === "brand-feature"), [sections]);
  const firstAutoBrandFeature = brandFeatureSections.find(section => section.layout === "horizontal");
  const hasBrandStrip = sections.some(section => section.kind === "brand-strip");
  const heroSection = sections.find(section => section.kind === "hero");
  const primarySectionId = heroSection?.id;
  const brandStripAnchorId = heroSection?.id
    ?? sections.find(section => section.kind !== "brand-strip")?.id
    ?? sections[0]?.id;
  const content = storefrontSettings.content;
  const homepageProducts = useMemo(() => prioritizeHomepageProducts(products), [products]);
  const homepageSaleProducts = useMemo(() => prioritizeHomepageProducts(saleCatalog.products), [saleCatalog.products]);
  const chosen = useMemo(() => homepageProducts.slice(0, 12), [homepageProducts]);
  const fallbackHeroImage = homepageProducts[0]?.images[0];
  const heroBrandsBySlug = useMemo(() => new Map<string, { slug: string; name: string; href: string }>(
    homepageSectionBrands
      .filter(brand => brand.productCount > 0)
      .map(brand => [brand.slug, { slug: brand.slug, name: brand.name, href: `/brands/${brand.slug}` }]),
  ), [homepageSectionBrands]);
  const heroShopButtons = useMemo(() => storefrontSettings.heroShopBrandSlugs.flatMap(slug => {
    const brand = heroBrandsBySlug.get(slug);
    if (!brand) return [];
    const configuredHref = storefrontSettings.brands.find(item => item.slug === slug)?.href;
    return [{ ...brand, href: configuredHref ?? brand.href }];
  }), [storefrontSettings.heroShopBrandSlugs, storefrontSettings.brands, heroBrandsBySlug]);
  if (hasConfiguredSections && sections.length) return <main className="page-enter">
    {sections.map(section => <Fragment key={section.id}>
      <HomepageBlock
        key={section.id}
        section={section}
        brands={section.kind === "shop-by-brand" ? shopByBrandCatalog : homepageSectionBrands}
        brandLayoutBrands={homepageBrands}
        heroShopButtons={heroShopButtons}
        fallbackHeroImage={fallbackHeroImage}
         heroSlides={section.kind === "hero" ? validHeroSlides : EMPTY_HERO_SLIDES}
         activeHeroIndex={section.kind === "hero" ? heroSlideIndex : 0}
         activeHeroSlide={section.kind === "hero" ? activeHeroSlide : undefined}
         heroLoading={section.kind === "hero" && homepageHero.isPending}
        onSelectHeroSlide={setHeroSlideIndex}
        onHeroPauseChange={setHeroRotationPaused}
        isPrimaryHeading={section.id === primarySectionId || (!heroSection && section.kind === "brand-strip")}
        brandSectionIndex={brandFeatureSections.findIndex(item => item.id === section.id)}
      />
      {section.kind === "brand-feature"
        && section.id === firstAutoBrandFeature?.id
        && homepageBrands.length === 2
        && brandFeatureSections.length === 1
        && section.brandSlug
        && homepageBrands.some(brand => brand.slug !== section.brandSlug)
        && <HomepageBlock
          section={{
            ...section,
            id: `auto-${homepageBrands.find(brand => brand.slug !== section.brandSlug)!.slug}-${section.id.slice(0, 48)}`,
            brandSlug: homepageBrands.find(brand => brand.slug !== section.brandSlug)!.slug,
            layout: "horizontal",
             productHandles: [],
            heading: "",
            subheading: "",
            ctaText: "",
            ctaHref: null,
             hiddenCopyFields: [],
          }}
          brands={homepageSectionBrands}
          brandLayoutBrands={homepageBrands}
          heroShopButtons={heroShopButtons}
          fallbackHeroImage={fallbackHeroImage}
           heroSlides={EMPTY_HERO_SLIDES}
           activeHeroIndex={0}
           activeHeroSlide={undefined}
           heroLoading={false}
          onSelectHeroSlide={setHeroSlideIndex}
          onHeroPauseChange={setHeroRotationPaused}
          isPrimaryHeading={false}
          brandSectionIndex={1}
        />}
        {!storefrontSettings.shopByBrandCopy.sectionManaged
         && !storefrontSettings.homepageSections?.some(item => item.kind === "shop-by-brand")
         && section.id === firstAutoBrandFeature?.id
         && homepageBrands.length >= 3
         && <HomepageShopByBrand brands={shopByBrandCatalog}/>}
      {!hasBrandStrip && section.id === brandStripAnchorId && <HomepageBrandStrip sectionId="automatic" isPrimaryHeading={!heroSection} mobileOnly={Boolean(heroSection)}/>}
    </Fragment>)}
    <Footer/>
  </main>;
  return <main className="page-enter">
    <section className="homepage-hero-section mx-auto grid min-h-[610px] max-w-[1440px] grid-cols-1 md:min-h-0 md:grid-cols-[.72fr_1.28fr]">
      <div className="homepage-hero-copy flex flex-col justify-center px-7 py-14 md:h-full md:justify-start md:px-12 md:py-6 lg:px-14">
        <div className="md:my-auto">
          <p className="storefront-info-text mono mb-8 text-[10px] tracking-[.16em] text-[#746e65]">{content.heroEyebrow}</p>
          <h1 className="serif max-w-[550px] whitespace-pre-line text-[58px] leading-[.98] tracking-[-.04em] sm:text-7xl lg:text-[92px]">{content.heroHeading.replace(/\.$/, "")}<span className="text-[#b06b55]">{content.heroHeading.endsWith(".") ? "." : ""}</span></h1>
          <p className="mt-7 max-w-[360px] whitespace-pre-line text-sm leading-7 text-[#5f5b55]">{content.heroSubheading}</p>
          <div className="mt-9 flex flex-wrap gap-3">{activeHeroSlide
            ? <Link href={activeHeroSlide.brandHref} className="solid-button text-[10px] tracking-[.16em]" data-testid={`link-home-shop-${activeHeroSlide.brandSlug}`}>Shop {activeHeroSlide.brandName} <ArrowRight size={15}/></Link>
            : heroShopButtons.map(brand => <Link key={brand.slug} href={brand.href} className="solid-button text-[10px] tracking-[.16em]" data-testid={`link-home-shop-${brand.slug}`}>Shop {brand.name} <ArrowRight size={15}/></Link>)}<Link href="/brands" className="outline-button text-[10px] tracking-[.16em]" data-testid="link-home-brands">ALL BRANDS</Link></div>
          <div className="storefront-info-text mt-16 flex items-center gap-3 text-[9px] tracking-[.1em] text-[#5f5b55] md:mt-8"><span className="h-px w-9 bg-[#5f5b55]"/>{content.heroBottomNote}</div>
        </div>
        <HomepageBrandStrip sectionId="default-inline" inline eyebrow="" heading="" subheading="" ctaText=""/>
      </div>
      <HomepageHeroVisual
        rotating={isHeroRotating}
        slides={validHeroSlides}
        activeIndex={heroSlideIndex}
        onSelectSlide={setHeroSlideIndex}
        onPauseChange={setHeroRotationPaused}
        staticImage={homepageHero.isPending ? undefined : content.heroImageUrl || fallbackHeroImage?.src}
        staticWidth={content.heroImageUrl ? undefined : fallbackHeroImage?.width}
        staticHeight={content.heroImageUrl ? undefined : fallbackHeroImage?.height}
        staticEyebrow={content.heroImageEyebrow}
        staticCaption={content.heroImageCaption}
        imageFocalPoints={storefrontSettings.imageFocalPoints}
      />
    </section>
    <HomepageBrandStrip sectionId="default-mobile" mobileOnly/>
    <section className="mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-28">
      <div className="mb-8 flex items-end justify-between"><div><p className="mono text-[9px] tracking-[.2em] text-[#5f5b55]">{content.featuredEyebrow}</p><h2 className="serif mt-3 text-4xl md:text-5xl">{content.featuredHeading}</h2></div><Link href="/brands/ethnc" className="hidden items-center gap-2 pb-1 text-[10px] tracking-[.12em] md:flex">VIEW ALL <ArrowRight size={14}/></Link></div>
      {loading ? <ProductSkeletons/> : error ? <div className="border-y border-[#d7d3cb] py-10 text-center text-sm">{error} <button className="ml-2 underline" onClick={() => window.location.reload()}>Try again</button></div> : chosen.length ? <div className="grid grid-cols-2 gap-x-3 gap-y-8 md:grid-cols-4 md:gap-x-6">{chosen.map((p, i) => <ProductCard key={p.id} product={p} index={i} loading="lazy"/>)}</div> : <p className="py-10 text-sm">The collection is being refreshed. Visit again soon.</p>}
      <Link href="/brands/ethnc" className="outline-button mt-9 w-full text-[10px] tracking-[.15em] md:hidden">EXPLORE ALL ETHNC <ArrowRight size={14}/></Link>
    </section>
    {saleEnabled && !saleCatalog.loading && homepageSaleProducts.length >= 4 && <section className="bg-[#181818] text-[#f8f5ef]" data-testid="homepage-sale-band">
      <div className="mx-auto max-w-[1440px] px-5 py-14 md:px-10 md:py-20">
        <div className="mb-7 flex items-end justify-between gap-4"><div><p className="mono text-[9px] tracking-[.2em] text-[#d5aa91]">{content.saleEyebrow}</p><h2 className="serif mt-3 text-4xl md:text-5xl">{content.saleHeading}</h2></div><Link href="/sale" className="hidden items-center gap-2 pb-1 text-[10px] tracking-[.12em] text-[#f8f5ef] md:flex">SHOP ALL SALE <ArrowRight size={14}/></Link></div>
        <div className="hide-scrollbar grid min-w-0 grid-cols-2 gap-x-3 gap-y-8 sm:grid-cols-3 md:flex md:snap-x md:snap-mandatory md:gap-4 md:overflow-x-auto md:pb-3">
          {homepageSaleProducts.slice(0, 8).map((product, index) => <div key={product.id} className="min-w-0 md:w-[31%] md:flex-none md:snap-start lg:w-[23%]"><ProductCard product={product} index={index} tone="dark" loading="lazy"/></div>)}
        </div>
        <Link href="/sale" className="outline-button mt-7 w-full border-[#f8f5ef] text-[10px] tracking-[.15em] text-[#f8f5ef] md:hidden">SHOP ALL SALE <ArrowRight size={14}/></Link>
      </div>
    </section>}
    <section className="bg-[#e7e2d9]">
      <div className="mx-auto grid max-w-[1440px] items-center gap-10 px-6 py-16 md:grid-cols-[1fr_1fr] md:px-12 md:py-24">
        <div className="max-w-xl"><p className="mono text-[10px] tracking-[.2em]">{content.storyEyebrow}</p><h2 className="serif mt-5 whitespace-pre-line text-4xl leading-tight md:text-6xl">{content.storyHeading}</h2></div>
        <div><p className="max-w-lg whitespace-pre-line text-sm leading-7 text-[#5b5751]">{content.storyBody}</p><Link href="/brands" className="mt-7 inline-flex items-center gap-3 border-b border-[#171717] pb-2 text-[10px] tracking-[.15em]">OUR BRANDS <ArrowRight size={14}/></Link></div>
      </div>
    </section>
    <HowItWorksSection section={{
      id: "how-it-works-fallback",
      kind: "editorial",
      enabled: true,
      layout: "featured-visual",
      brandSlug: null,
      productCount: 4,
      productHandles: [],
      imageUrl: null,
      imageEyebrow: "",
      imageCaption: "",
      eyebrow: "A LITTLE MORE CERTAIN",
      heading: "How it works",
      subheading: JSON.stringify(DEFAULT_HOW_IT_WORKS_STEPS),
      ctaText: "",
      ctaHref: null,
      hiddenCopyFields: [],
    }} isPrimaryHeading={false}/>
    <Footer/>
  </main>;
}

function optimizedHeroImageSources(source: string) {
  return responsiveShopifyImageSources(source, HERO_IMAGE_WIDTHS, 960);
}

function applySavedBrandOrder<T extends { slug: string }>(brands: T[], order: Array<{ slug: string; visible: boolean }>) {
  const rank = new Map(order.map((entry, index) => [entry.slug, index]));
  return brands
    .map((brand, sourceIndex) => ({ brand, sourceIndex }))
    .filter(({ brand }) => order.find(entry => entry.slug === brand.slug)?.visible !== false)
    .sort((a, b) => (rank.get(a.brand.slug) ?? Number.MAX_SAFE_INTEGER) - (rank.get(b.brand.slug) ?? Number.MAX_SAFE_INTEGER)
      || a.sourceIndex - b.sourceIndex)
    .map(({ brand }) => brand);
}

function HomepageHeroVisual({
  rotating,
  slides,
  activeIndex,
  onSelectSlide,
  onPauseChange,
  staticImage,
  staticWidth,
  staticHeight,
  staticEyebrow,
  staticCaption,
  imageFocalPoints,
  containerClassName = "homepage-hero-media",
  gradientClassName = "pointer-events-none absolute inset-x-0 bottom-0 h-[38%] bg-gradient-to-t from-black/50 via-black/15 to-transparent",
  captionClassName = "absolute bottom-7 left-7 text-white md:bottom-10 md:left-10",
}: {
  rotating: boolean;
  slides: Array<{
    brandSlug: string;
    brandName: string;
    brandHref: string;
    productId: string;
    productTitle: string;
    productHref: string;
    imageUrl: string;
    imageWidth: number;
    imageHeight: number;
    priceBDT: number;
  }>;
  activeIndex: number;
  onSelectSlide: (index: number) => void;
  onPauseChange: (paused: boolean) => void;
  staticImage?: string | null;
  staticWidth?: number;
  staticHeight?: number;
  staticEyebrow: string;
  staticCaption: string;
  imageFocalPoints: StorefrontSettings["imageFocalPoints"];
  containerClassName?: string;
  gradientClassName?: string;
  captionClassName?: string;
}) {
  const touchPauseTimer = useRef<number | null>(null);
  const displayedIndexRef = useRef(activeIndex);
  const [displayedIndex, setDisplayedIndex] = useState(activeIndex);
  const [outgoingIndex, setOutgoingIndex] = useState<number | null>(null);
  const [transitionActive, setTransitionActive] = useState(true);
  const activeSlide = rotating ? slides[displayedIndex % slides.length] : undefined;
  const staticFocalPoint = getImageFocalPosition(staticImage, imageFocalPoints);
  const staticImageSources = staticImage ? optimizedHeroImageSources(staticImage) : null;

  useEffect(() => () => {
    if (touchPauseTimer.current !== null) window.clearTimeout(touchPauseTimer.current);
  }, []);

  useEffect(() => {
    if (!rotating || !slides.length) {
      displayedIndexRef.current = 0;
      setDisplayedIndex(0);
      setOutgoingIndex(null);
      setTransitionActive(true);
      return;
    }
    if (activeIndex === displayedIndexRef.current) return;
    const previousIndex = displayedIndexRef.current;
    displayedIndexRef.current = activeIndex;
    setOutgoingIndex(previousIndex);
    setDisplayedIndex(activeIndex);
    setTransitionActive(false);
    const frame = window.requestAnimationFrame(() => setTransitionActive(true));
    const timer = window.setTimeout(() => setOutgoingIndex(null), 760);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [activeIndex, rotating, slides.length]);

  function pauseAfterTouch() {
    onPauseChange(true);
    if (touchPauseTimer.current !== null) window.clearTimeout(touchPauseTimer.current);
    touchPauseTimer.current = window.setTimeout(() => onPauseChange(false), 5000);
  }

  return <div
    className={containerClassName}
    onMouseEnter={() => onPauseChange(true)}
    onMouseLeave={() => onPauseChange(false)}
    onTouchStart={pauseAfterTouch}
    onTouchEnd={() => {
      if (touchPauseTimer.current !== null) window.clearTimeout(touchPauseTimer.current);
      touchPauseTimer.current = window.setTimeout(() => onPauseChange(false), 5000);
    }}
    data-testid="homepage-hero-visual"
  >
    {activeSlide
      ? <>
          {outgoingIndex !== null && slides[outgoingIndex] && <HeroSlideImage slide={slides[outgoingIndex]} focalPoint={getImageFocalPosition(slides[outgoingIndex].imageUrl, imageFocalPoints)} priority={outgoingIndex === 0} outgoing transitionActive={transitionActive}/>}
          <HeroSlideImage slide={activeSlide} focalPoint={getImageFocalPosition(activeSlide.imageUrl, imageFocalPoints)} priority={displayedIndex === 0} transitionActive={transitionActive} visible={outgoingIndex === null || transitionActive}/>
        </>
      : staticImage && <img
        src={staticImageSources?.src ?? staticImage}
        srcSet={staticImageSources?.srcSet}
        sizes={staticImageSources?.srcSet ? "(max-width: 767px) 100vw, 58vw" : undefined}
        width={staticWidth ?? 1200}
        height={staticHeight ?? 1500}
        alt="Chamakpatti featured fashion"
        loading="eager"
        fetchPriority="high"
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover"
        style={{ objectPosition: `${staticFocalPoint.xPercent}% ${staticFocalPoint.yPercent}%` }}
      />}
    <div className={gradientClassName}/>
    <div className={`${captionClassName} z-10`}>
      <p className="mono text-[9px] tracking-[.2em]">{activeSlide ? `THE ${activeSlide.brandName.toUpperCase()} EDIT` : staticEyebrow}</p>
      {activeSlide
        ? <p className="serif mt-2 whitespace-pre-line text-3xl">{activeSlide.productTitle}</p>
        : <p className="serif mt-2 whitespace-pre-line text-3xl">{staticCaption}</p>}
    </div>
    <div className="absolute right-5 top-5 flex h-16 w-16 items-center justify-center rounded-full border border-white/70 text-white"><ArrowDown size={18}/></div>
    {rotating && <div className="absolute bottom-5 right-6 flex items-center gap-2" role="group" aria-label="Featured brand slides">
      {slides.map((slide, index) => <button
        key={`${slide.brandSlug}-${slide.productId}`}
        type="button"
        aria-label={`Show ${slide.brandName} slide`}
        aria-current={activeIndex === index}
        onClick={() => onSelectSlide(index)}
        className="homepage-hero-dot"
        data-testid={`button-home-hero-slide-${index + 1}`}
      />)}
    </div>}
  </div>;
}

function HeroSlideImage({
  slide,
  focalPoint,
  priority,
  outgoing = false,
  transitionActive,
  visible = false,
}: {
  slide: {
    productId: string;
    productTitle: string;
    imageUrl: string;
    imageWidth: number;
    imageHeight: number;
  };
  focalPoint: { xPercent: number; yPercent: number };
  priority: boolean;
  outgoing?: boolean;
  transitionActive: boolean;
  visible?: boolean;
}) {
  const shown = outgoing ? !transitionActive : visible;
  const initialPriorityPaint = useRef(priority && !outgoing);
  useEffect(() => {
    initialPriorityPaint.current = false;
  }, []);
  const imageSources = optimizedHeroImageSources(slide.imageUrl);
  return <div
    aria-hidden={outgoing}
    className={`homepage-hero-layer absolute inset-0 block${shown ? " is-visible" : ""}${initialPriorityPaint.current ? " is-first-paint" : ""}${outgoing ? " is-outgoing" : ""}`}
    style={outgoing ? { pointerEvents: "none" } : undefined}
  >
    <img
      key={slide.productId}
      src={imageSources.src}
      srcSet={imageSources.srcSet}
      sizes={imageSources.srcSet ? "(max-width: 767px) 100vw, 58vw" : undefined}
      width={slide.imageWidth}
      height={slide.imageHeight}
      alt=""
      loading={priority ? "eager" : "lazy"}
      fetchPriority={priority ? "high" : "auto"}
      decoding="async"
      className="h-full w-full object-cover object-center"
      style={{ objectPosition: `${focalPoint.xPercent}% ${focalPoint.yPercent}%` }}
    />
  </div>;
}

function HomepageBrandStrip({
  sectionId,
  eyebrow = "THE LABELS WE LOVE",
  heading = "",
  subheading = "",
  ctaText = "",
  ctaHref = "/brands",
  isPrimaryHeading = false,
  inline = false,
  mobileOnly = false,
}: {
  sectionId: string;
  eyebrow?: string;
  heading?: string;
  subheading?: string;
  ctaText?: string;
  ctaHref?: string;
  isPrimaryHeading?: boolean;
  inline?: boolean;
  mobileOnly?: boolean;
}) {
  const { storefrontSettings } = useStore();
  const StripLabel = isPrimaryHeading ? "h1" : "p";
  const featuredBrands = [
    { slug: "ethnc", name: "Ethnc" },
    { slug: "khaadi", name: "Khaadi" },
    { slug: "sapphire", name: "Sapphire" },
    { slug: "studiosy", name: "StudioSY" },
    { slug: "qalamkar", name: "Qalamkar" },
    { slug: "beechtree", name: "Beechtree" },
    { slug: "lulusar", name: "Lulusar" },
  ];

  const stripContent = <>
      {(eyebrow || heading || subheading) && <div className="mx-auto mb-8 max-w-2xl text-center md:mb-10">
        {eyebrow && <StripLabel className="mono text-center text-[9px] tracking-[.24em] text-[#815348]">{eyebrow}</StripLabel>}
        {heading && <h2 className="serif mt-3 text-3xl leading-tight text-[#49352f] md:text-4xl">{heading}</h2>}
        {subheading && <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-[#68635d]">{subheading}</p>}
      </div>}
      <p className="sr-only">Featured labels: {featuredBrands.map(brand => brand.name).join(", ")}.</p>
      <div className="brand-marquee" aria-hidden="true">
        <div className="brand-marquee__track">
          {[false, true].map(duplicate => <div key={duplicate ? "duplicate" : "primary"} className={`brand-marquee__set${duplicate ? " brand-marquee__set--duplicate" : ""}`}>
            {featuredBrands.map(brand => {
              const logoUrl = storefrontSettings.brandMedia.find(media => media.slug === brand.slug)?.logoUrl
                ?? INITIAL_FEATURED_BRAND_LOGOS[brand.slug];
              return <div
                key={brand.slug}
                className="brand-marquee__logo"
                data-testid={duplicate ? undefined : `featured-label-${brand.slug}`}
              >
                {logoUrl
                  ? <img src={logoUrl} alt="" loading="lazy" decoding="async"/>
                  : <span className="serif text-lg text-[#3f3934] md:text-xl">{brand.name}</span>}
              </div>;
            })}
          </div>)}
        </div>
      </div>
      {ctaText && <div className="mt-6 text-center"><Link href={ctaHref} className="inline-flex min-h-11 items-center gap-2 border-b border-[#49352f] pb-1 text-[10px] tracking-[.14em]">{ctaText}<ArrowRight size={14}/></Link></div>}
  </>;
  if (inline) return <div className="homepage-brand-strip-inline" data-testid={`homepage-section-${sectionId}`}>
    {stripContent}
  </div>;
  return <section
    className={`bg-[#f8f5ef] pb-14 pt-14 md:py-20 ${mobileOnly ? "homepage-brand-strip-mobile" : ""}`}
    aria-label="The labels we love"
    data-testid={`homepage-section-${sectionId}`}
  >
    <div className="mx-auto max-w-[1440px] px-5 md:px-10">{stripContent}</div>
  </section>;
}

function BrandFeatureSection({
  section,
  brand,
  products,
  description,
  mirrored,
  shade,
  pending,
  isPrimaryHeading,
  ctaHref,
}: {
  section: HomepageSection;
  brand: PublicCatalogBrand;
  products: StoreProduct[];
  description?: string;
  mirrored: boolean;
  shade: boolean;
  pending: boolean;
  isPrimaryHeading: boolean;
  ctaHref: string;
}) {
  const railRef = useRef<HTMLDivElement>(null);
  const Heading = isPrimaryHeading ? "h1" : "h2";
  const productCardWidth = mirrored
    ? "w-[clamp(220px,82vw,300px)] md:w-[clamp(240px,23.2vw,335px)]"
    : "w-[46%] min-w-[150px] sm:w-[35%] md:w-[43%]";
  const eyebrow = resolvedSectionCopy(section, "eyebrow", section.eyebrow, brand.name, brand.name);
  const heading = resolvedSectionCopy(section, "heading", section.heading, brand.name, brand.name);
  const subheading = resolvedSectionCopy(
    section,
    "subheading",
    section.subheading,
    description || "A considered edit from the labels we love, selected for the way it makes everyday feel more alive.",
    brand.name,
  );
  const ctaText = resolvedSectionCopy(section, "ctaText", section.ctaText, `SHOP ${brand.name}`, brand.name);
  return <section className={shade ? "bg-[#eee9df]" : "bg-[#f8f5ef]"} data-testid={`homepage-section-${section.id}`}>
      <div className={`mx-auto grid max-w-[1440px] gap-8 px-5 py-12 md:items-center md:py-16 ${mirrored ? "md:grid-cols-[minmax(0,1fr)_clamp(205px,20vw,280px)] md:gap-4 md:px-2" : "md:grid-cols-[.72fr_1.28fr] md:gap-10 md:px-10"}`}>
      <div className={`flex flex-col items-start ${mirrored ? "md:order-2" : ""}`}>
        {eyebrow && <p className="mono text-[9px] tracking-[.2em] text-[#815348]">{eyebrow}</p>}
        {heading && <Heading className={`serif mt-3 text-4xl leading-tight ${mirrored ? "md:text-4xl xl:text-5xl" : "md:text-5xl"}`}>{heading}</Heading>}
        {subheading && <p className="mt-3 max-w-[36ch] text-sm leading-6 text-[#68635d]">{subheading}</p>}
        {ctaText && <Link href={ctaHref} className="mt-5 inline-flex items-center gap-2 border-b border-[#49352f] pb-2 text-[10px] tracking-[.15em]" data-testid={`link-section-cta-${section.id}`}>{ctaText} <ArrowRight size={14}/></Link>}
      </div>
      <div className={`min-w-0 ${mirrored ? "md:order-1" : ""}`}>
        <div className="mb-3 flex justify-end gap-2">
          <button type="button" onClick={() => railRef.current?.scrollBy({ left: -300, behavior: "smooth" })} aria-label={`Show previous ${brand.name} products`} className="flex h-11 w-11 items-center justify-center border border-[#cfc8bc] text-[#49352f] hover:bg-[#e8e1d7]" data-testid={`button-home-brand-previous-${section.id}`}><ArrowLeft size={16}/></button>
          <button type="button" onClick={() => railRef.current?.scrollBy({ left: 300, behavior: "smooth" })} aria-label={`Show more ${brand.name} products`} className="flex h-11 w-11 items-center justify-center border border-[#cfc8bc] text-[#49352f] hover:bg-[#e8e1d7]" data-testid={`button-home-brand-next-${section.id}`}><ArrowRight size={16}/></button>
        </div>
        {pending && !products.length
          ? <div className="flex gap-4 overflow-hidden">{Array.from({ length: mirrored ? 4 : 2 }, (_, index) => <div key={index} className={`${productCardWidth} flex-none`}><div className="skeleton aspect-[.73]"/><div className="skeleton mt-3 h-4 w-3/4"/><div className="skeleton mt-2 h-3 w-1/3"/></div>)}</div>
         : <div ref={railRef} className="hide-scrollbar flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth pb-3" aria-label={`${brand.name} featured products`} data-testid={`rail-home-brand-${section.id}`}>
               {products.map((product, index) => <div key={`${brand.slug}-${product.id}`} className={`${productCardWidth} flex-none snap-start`}><ProductCard product={product} index={index} loading="lazy"/></div>)}
            </div>}
      </div>
    </div>
  </section>;
}

function BrandShopByEdit({ section, brand, categories }: {
  section: HomepageSection;
  brand: PublicCatalogBrand;
  categories: string[];
}) {
  const { storefrontSettings } = useStore();
  const [tiles, setTiles] = useState<Array<{ category: string; image: string }>>([]);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [pending, setPending] = useState(true);
  const eyebrow = resolvedSectionCopy(section, "eyebrow", section.eyebrow, brand.name, brand.name);
  const heading = resolvedSectionCopy(section, "heading", section.heading, "Shop by edit", brand.name);
  const subheading = resolvedSectionCopy(section, "subheading", section.subheading, "", brand.name);
  const ctaText = resolvedSectionCopy(section, "ctaText", section.ctaText, "VIEW ALL", brand.name);
  const categoryKey = [...new Set(categories.map(category => category.trim()).filter(Boolean))].join("|");

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    let hasRequestError = false;
    const categoryNames = categoryKey.split("|").filter(Boolean);
    setPending(true);
    setHasLoadError(false);
    setTiles([]);
    void Promise.all(categoryNames.map(async category => {
      try {
        const page = brand.slug === "ethnc"
          ? await listEasternProducts({
              page: 1,
              sort: "featured",
              category: category as ListEasternProductsCategory,
            }, { signal: controller.signal })
          : await listBrandCatalogProducts({
              brandSlug: brand.slug,
              page: 1,
              sort: "featured",
              category,
            }, { signal: controller.signal });
        const firstAvailable = prioritizeHomepageProducts(page.products as StoreProduct[]).find(product =>
          (storefrontSettings.showSoldOutProductsInLists || product.variants.some(variant => variant.available))
            && product.images[0]?.src,
        );
        return firstAvailable?.images[0]?.src ? { category, image: firstAvailable.images[0].src } : null;
      } catch (error) {
        hasRequestError = true;
        console.error("Homepage category image request failed", error);
        return null;
      }
    })).then(results => {
      if (!cancelled) {
        setTiles(results.filter((tile): tile is { category: string; image: string } => Boolean(tile)));
        setHasLoadError(hasRequestError);
      }
    }).finally(() => {
      if (!cancelled) setPending(false);
    });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [brand.slug, categoryKey, storefrontSettings.showSoldOutProductsInLists]);

  if (!pending && !tiles.length && !hasLoadError) return null;
  return <section className="bg-[#f8f5ef]" data-testid={`homepage-section-${section.id}`}>
    <div className="mx-auto max-w-[1440px] px-5 py-12 md:px-10 md:py-16">
      <div className="mb-7 flex items-end justify-between gap-4">
         <div>{eyebrow && <p className="mono text-[9px] tracking-[.2em] text-[#815348]">{eyebrow}</p>}{heading && <h2 className="serif mt-3 text-4xl md:text-5xl">{heading}</h2>}{subheading && <p className="mt-3 max-w-xl text-sm leading-6 text-[#68635d]">{subheading}</p>}</div>
         {ctaText && <Link href={section.ctaHref || `/brands/${brand.slug}`} className="hidden items-center gap-2 pb-1 text-[10px] tracking-[.12em] md:flex">{ctaText} <ArrowRight size={14}/></Link>}
      </div>
      {pending
        ? <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{Array.from({ length: Math.min(Math.max(categories.length, 2), 4) }, (_, index) => <div key={index} className="skeleton aspect-[.9]"/>)}</div>
        : <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {tiles.map(tile => {
              const href = brand.slug === "ethnc"
                ? categoryHref(tile.category)
                : `/brands/${brand.slug}?category=${encodeURIComponent(tile.category)}`;
              return <Link key={tile.category} href={href} className="group relative block aspect-[.9] overflow-hidden bg-[#e7e2d9]" data-testid={`link-home-edit-${brand.slug}-${slugify(tile.category)}`}>
                <img src={tile.image} alt={`${brand.name} ${tile.category}`} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"/>
                <span className="absolute bottom-3 left-3 bg-[#f8f5ef]/95 px-3 py-2 mono text-[10px] font-semibold tracking-[.1em] text-[#34231d] shadow-sm">{tile.category.toUpperCase()}</span>
              </Link>;
            })}
          </div>}
      {hasLoadError && <p role="status" className="mt-4 text-xs text-[#815348]">Some edit images are temporarily unavailable.</p>}
      {ctaText && <Link href={section.ctaHref || `/brands/${brand.slug}`} className="outline-button mt-7 w-full text-[10px] tracking-[.15em] md:hidden">{ctaText} <ArrowRight size={14}/></Link>}
    </div>
  </section>;
}

function HomepageShopByBrand({ brands, section }: { brands: PublicCatalogBrand[]; section?: HomepageSection }) {
  const { storefrontSettings } = useStore();
  const homepageSettings = useGetStorefrontHomepageSettings({ query: { queryKey: getGetStorefrontHomepageSettingsQueryKey() } });
  const savedOrder = homepageSettings.data?.brandOrder ?? storefrontSettings.brandOrder ?? [];
  const rankedBrands = applySavedBrandOrder(brands, savedOrder);
  const visibleBrands = section?.brandSlugs === undefined
    ? rankedBrands
    : rankedBrands.filter(brand => section.brandSlugs?.includes(brand.slug));
  const [selectedSlug, setSelectedSlug] = useState(visibleBrands[0]?.slug ?? "");
  const [rotationPaused, setRotationPaused] = useState(false);
  const [tabHidden, setTabHidden] = useState(false);
  const [manuallySelected, setManuallySelected] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const touchPauseTimeout = useRef<number | null>(null);
  useEffect(() => () => {
    if (touchPauseTimeout.current !== null) window.clearTimeout(touchPauseTimeout.current);
  }, []);
  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  const rotationEnabled = (homepageSettings.data?.shopByBrandRotationEnabled ?? storefrontSettings.shopByBrandRotationEnabled ?? true)
    && visibleBrands.length > 1 && !reducedMotion && !rotationPaused && !tabHidden && !manuallySelected;
  useEffect(() => {
    if (!rotationEnabled) return;
    const interval = Math.max(3, homepageSettings.data?.shopByBrandRotationIntervalSeconds ?? storefrontSettings.shopByBrandRotationIntervalSeconds ?? 6) * 1000;
    const timer = window.setInterval(() => {
      setSelectedSlug(current => {
        const index = visibleBrands.findIndex(brand => brand.slug === current);
        return visibleBrands[(index + 1) % visibleBrands.length]?.slug ?? current;
      });
    }, interval);
    return () => window.clearInterval(timer);
  }, [rotationEnabled, visibleBrands, homepageSettings.data?.shopByBrandRotationIntervalSeconds, storefrontSettings.shopByBrandRotationIntervalSeconds]);
  useEffect(() => {
    const onVisibility = () => setTabHidden(document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);
  const activeSlug = visibleBrands.some(brand => brand.slug === selectedSlug) ? selectedSlug : visibleBrands[0]?.slug ?? "";
  const activeBrand = visibleBrands.find(brand => brand.slug === activeSlug);
  const currentMedia = storefrontSettings.brandMedia.find(media => media.slug === activeSlug);
  const currentBrandImage = currentMedia?.featuredImageUrl || currentMedia?.bannerImageUrl;
  const currentBrandImageSources = currentBrandImage
    ? responsiveShopifyImageSources(currentBrandImage, BRAND_FEATURE_IMAGE_WIDTHS, 960)
    : null;
  const brandParams = { brandSlug: activeSlug, page: 1, sort: "featured" as const };
  const brandProducts = useListBrandCatalogProducts(brandParams, {
    query: {
      enabled: Boolean(activeSlug && activeSlug !== "ethnc"),
      queryKey: getListBrandCatalogProductsQueryKey(brandParams),
    },
  });
  const easternParams = { page: 1, sort: "featured" as const };
  const easternProducts = useListEasternProducts(easternParams, {
    query: {
      enabled: activeSlug === "ethnc",
      queryKey: getListEasternProductsQueryKey(easternParams),
    },
  });
  const catalogItems = activeSlug === "ethnc"
    ? (easternProducts.data?.products ?? []).map(product => product as StoreProduct)
    : (brandProducts.data?.products ?? []).map(product => ({
        ...product,
        images: product.images.map(image => ({ ...image })),
        variants: product.variants.map(variant => ({ ...variant })),
      } as StoreProduct));
  const availableProducts = storefrontSettings.showSoldOutProductsInLists
    ? catalogItems
    : catalogItems.filter(product => product.variants.some(variant => variant.available));
  const visibleProducts = prioritizeHomepageProducts(availableProducts);
  const pending = activeSlug === "ethnc" ? easternProducts.isLoading : brandProducts.isLoading;
  if (!activeBrand) return null;
  const allHref = section?.ctaHref
    || storefrontSettings.brands.find(brand => brand.slug === activeSlug)?.href
    || `/brands/${activeSlug}`;
  const copy = storefrontSettings.shopByBrandCopy;
  const eyebrow = section
    ? resolvedSectionCopy(section, "eyebrow", section.eyebrow, "FIND YOUR LABEL", activeBrand.name)
    : resolveHomepageCopy(copy.eyebrow, "FIND YOUR LABEL", copy.hiddenCopyFields.includes("eyebrow"), activeBrand.name);
  const heading = section
    ? resolvedSectionCopy(section, "heading", section.heading, "Shop by brand", activeBrand.name)
    : resolveHomepageCopy(copy.heading, "Shop by brand", copy.hiddenCopyFields.includes("heading"), activeBrand.name);
  const subheading = section
    ? resolvedSectionCopy(section, "subheading", section.subheading, "", activeBrand.name)
    : resolveHomepageCopy(copy.subheading, "", copy.hiddenCopyFields.includes("subheading"), activeBrand.name);
  const ctaText = section
    ? resolvedSectionCopy(section, "ctaText", section.ctaText, "VIEW ALL {brand}", activeBrand.name.toUpperCase())
    : resolveHomepageCopy(copy.ctaText, "VIEW ALL {brand}", copy.hiddenCopyFields.includes("ctaText"), activeBrand.name.toUpperCase());
  const hasCopy = Boolean(eyebrow || heading || subheading || ctaText);
  const sectionId = section?.id ?? "legacy";
  return <section className="bg-[#eee9df]" data-testid={section ? `homepage-section-${section.id}` : "homepage-shop-by-brand"} onMouseEnter={() => setRotationPaused(true)} onMouseLeave={() => setRotationPaused(false)} onFocusCapture={() => setRotationPaused(true)} onBlurCapture={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setRotationPaused(false); }} onTouchStart={() => { setRotationPaused(true); if (touchPauseTimeout.current !== null) window.clearTimeout(touchPauseTimeout.current); }} onTouchEnd={() => { if (touchPauseTimeout.current !== null) window.clearTimeout(touchPauseTimeout.current); touchPauseTimeout.current = window.setTimeout(() => setRotationPaused(false), 5000); }}>
    <div className="mx-auto max-w-[1440px] px-5 py-12 md:px-10 md:py-16">
      <div className={`flex flex-col gap-5 md:flex-row md:items-end md:justify-between ${hasCopy ? "mb-6" : "mb-0"}`}>
         <div>{eyebrow && <p className="mono text-[9px] tracking-[.2em] text-[#815348]">{eyebrow}</p>}{heading && <h2 className="serif mt-3 text-4xl md:text-5xl">{heading}</h2>}{subheading && <p className="mt-3 max-w-xl text-sm leading-6 text-[#68635d]">{subheading}</p>}</div>
         {ctaText && <Link href={allHref} className="inline-flex items-center gap-2 pb-1 text-[10px] tracking-[.12em]" data-testid={section ? `link-home-brand-all-${section.id}-${activeSlug}` : `link-home-brand-all-${activeSlug}`}>{ctaText} <ArrowRight size={14}/></Link>}
      </div>
       <nav aria-label="Shop by brand" className="hide-scrollbar mb-7 flex gap-5 overflow-x-auto border-b border-[#d5cfc4] text-[10px] tracking-[.12em]">
         {visibleBrands.map(brand => <button key={brand.slug} type="button" onClick={() => { setSelectedSlug(brand.slug); setManuallySelected(true); }} aria-pressed={activeSlug === brand.slug} className={`inline-flex min-h-11 items-center whitespace-nowrap border-b-2 px-2 py-3 ${activeSlug === brand.slug ? "border-[#b06b55] text-[#49352f]" : "border-transparent text-[#5f5b55]"}`} data-testid={section ? `tab-home-brand-${section.id}-${brand.slug}` : `tab-home-brand-${brand.slug}`}>{brand.name.toUpperCase()}</button>)}
      </nav>
        {currentBrandImage && <div className="relative mb-7 aspect-[2.15/1] min-h-[180px] overflow-hidden bg-[#d9d2c7] md:aspect-[3.3/1] md:min-h-[250px]" data-testid={`img-shop-brand-feature-${activeSlug}`}>
          <img key={activeSlug} src={currentBrandImageSources?.src ?? currentBrandImage} srcSet={currentBrandImageSources?.srcSet} sizes={currentBrandImageSources?.srcSet ? "(max-width: 767px) 100vw, 58vw" : undefined} alt={`${activeBrand.name} collection`} className="shop-brand-crossfade absolute inset-0 h-full w-full object-cover" loading="lazy" fetchPriority="low" decoding="async"/>
         <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-black/45 via-black/10 to-transparent"/>
         <div className="absolute bottom-5 left-5 text-white md:bottom-8 md:left-8"><p className="mono text-[9px] tracking-[.2em]">THE {activeBrand.name.toUpperCase()} EDIT</p><p className="serif mt-2 text-3xl md:text-5xl">{activeBrand.name}</p></div>
       </div>}
      {pending
        ? <ProductSkeletons/>
        : visibleProducts.length
           ? <div className="grid grid-cols-2 gap-x-3 gap-y-8 md:grid-cols-4 md:gap-x-6" data-testid={section ? `grid-home-brand-${section.id}-${activeSlug}` : `grid-home-brand-${activeSlug}`}>{visibleProducts.slice(0, section?.productCount ?? 8).map((product, index) => <ProductCard key={`${activeSlug}-${product.id}`} product={product} index={index} loading="lazy"/>)}</div>
          : <p className="py-8 text-sm text-[#68635d]">No pieces are available from this label right now.</p>}
    </div>
  </section>;
}

function SaleBand({ section, products, pending, isPrimaryHeading }: {
  section: HomepageSection;
  products: StoreProduct[];
  pending: boolean;
  isPrimaryHeading: boolean;
}) {
  const { storefrontSettings } = useStore();
  if (pending || products.length < 4) return null;
  const Heading = isPrimaryHeading ? "h1" : "h2";
  const eyebrow = resolvedSectionCopy(section, "eyebrow", section.eyebrow, storefrontSettings.content.saleEyebrow);
  const heading = resolvedSectionCopy(section, "heading", section.heading, "The Sale Edit");
  const subheading = resolvedSectionCopy(section, "subheading", section.subheading, "");
  const ctaText = resolvedSectionCopy(section, "ctaText", section.ctaText, "SHOP ALL SALE");
  return <section className="bg-[#181818] text-[#f8f5ef]" data-testid={`homepage-sale-band-${section.id}`}>
    <div className="mx-auto max-w-[1440px] px-5 py-12 md:px-10 md:py-16">
      <div className="mb-7 flex items-end justify-between gap-4"><div>{eyebrow && <p className="mono text-[9px] tracking-[.2em] text-[#d5aa91]">{eyebrow}</p>}{heading && <Heading className="serif mt-3 text-4xl md:text-5xl">{heading}</Heading>}{subheading && <p className="mt-3 max-w-xl text-sm leading-6 text-[#dfd0c3]">{subheading}</p>}</div>{ctaText && <Link href={section.ctaHref || "/sale"} className="hidden items-center gap-2 pb-1 text-[10px] tracking-[.12em] md:flex">{ctaText} <ArrowRight size={14}/></Link>}</div>
      <div className="hide-scrollbar grid min-w-0 grid-cols-2 gap-x-3 gap-y-8 sm:grid-cols-3 md:flex md:snap-x md:snap-mandatory md:gap-4 md:overflow-x-auto md:pb-3">
        {products.slice(0, 8).map((product, index) => <div key={product.id} className="min-w-0 md:w-[31%] md:flex-none md:snap-start lg:w-[23%]"><ProductCard product={product} index={index} tone="dark" loading="lazy"/></div>)}
      </div>
      {ctaText && <Link href={section.ctaHref || "/sale"} className="outline-button mt-7 w-full border-[#f8f5ef] text-[10px] tracking-[.15em] text-[#f8f5ef] md:hidden">{ctaText} <ArrowRight size={14}/></Link>}
    </div>
  </section>;
}

function HowItWorksSection({ section, isPrimaryHeading }: { section: HomepageSection; isPrimaryHeading: boolean }) {
  const Heading = isPrimaryHeading ? "h1" : "h2";
  const steps = parseHowItWorksSteps(section.subheading);
  const eyebrow = resolvedSectionCopy(section, "eyebrow", section.eyebrow, "A LITTLE MORE CERTAIN");
  const heading = resolvedSectionCopy(section, "heading", section.heading, "How it works");
  return <section className="bg-[#f8f5ef]" data-testid={`homepage-section-${section.id}`}>
    <div className="mx-auto max-w-[1440px] px-5 py-12 md:px-10 md:py-16">
      <div className="mb-8 text-center">{eyebrow && <p className="mono text-[9px] tracking-[.2em] text-[#815348]">{eyebrow}</p>}{heading && <Heading className="serif mt-3 text-4xl md:text-5xl">{heading}</Heading>}</div>
      {!sectionCopyIsHidden(section, "subheading") && <div className="grid grid-cols-2 gap-x-5 gap-y-8 md:grid-cols-4 md:gap-8">
        {steps.map((step, index) => <article key={index} className="text-center">
          <div className="mb-4 flex h-10 items-center justify-center text-[#49352f]" aria-hidden="true">
            {index === 0 ? <span className="h-3.5 w-3.5 rotate-45 bg-[#49352f]"/> : index === 1 ? <span className="h-4 w-4 rounded-full bg-[#49352f]"/> : index === 2 ? <span className="h-3.5 w-3.5 rotate-45 border-2 border-[#49352f]"/> : <span className="serif text-3xl leading-none">✦</span>}
          </div>
          <h3 className="serif text-xl leading-tight md:text-2xl">{step.heading}</h3>
          <p className="mx-auto mt-2 max-w-[28ch] text-xs leading-5 text-[#68635d]">{step.body}</p>
        </article>)}
      </div>}
    </div>
  </section>;
}

const HomepageBlock = memo(function HomepageBlock({
  section,
  brands,
  brandLayoutBrands,
  heroShopButtons,
  fallbackHeroImage,
  heroSlides,
  activeHeroIndex,
  activeHeroSlide,
  heroLoading,
  onSelectHeroSlide,
  onHeroPauseChange,
  isPrimaryHeading,
  brandSectionIndex,
}: {
  section: HomepageSection;
  brands: PublicCatalogBrand[];
  brandLayoutBrands: PublicCatalogBrand[];
  heroShopButtons: { slug: string; name: string; href: string }[];
  fallbackHeroImage?: StoreProduct["images"][number];
  heroSlides: HomepageHeroSlide[];
  activeHeroIndex: number;
  activeHeroSlide?: HomepageHeroSlide;
  heroLoading: boolean;
  onSelectHeroSlide: (index: number) => void;
  onHeroPauseChange: (paused: boolean) => void;
  isPrimaryHeading: boolean;
  brandSectionIndex: number;
}) {
  const { storefrontSettings } = useStore();
  const isEasternBrand = !section.brandSlug || section.brandSlug === "ethnc";
  const hasCatalogBrand = Boolean(section.brandSlug && section.brandSlug !== "ethnc");
  const params = {
    brandSlug: section.brandSlug ?? "",
    page: 1,
    sort: section.kind === "sale" ? "discount" as const : "featured" as const,
    ...(section.productHandles.length ? { handles: section.productHandles.join(",") } : {}),
  };
  const brandProducts = useListBrandCatalogProducts(params, {
    query: {
      enabled: hasCatalogBrand && ["brand-feature", "product-collection"].includes(section.kind),
      queryKey: getListBrandCatalogProductsQueryKey(params),
    },
  });
  const easternParams = { page: 1, sort: "featured" as const, ...(section.productHandles.length ? { handles: section.productHandles.join(",") } : {}) };
  const easternProducts = useListEasternProducts(easternParams, {
    query: {
      enabled: isEasternBrand && ["brand-feature", "product-collection"].includes(section.kind),
      queryKey: getListEasternProductsQueryKey(easternParams),
      staleTime: 15_000,
    },
  });
  const saleParams = {
    page: 1,
    sort: "featured" as const,
    ...(section.brandSlug ? { brandSlug: section.brandSlug } : {}),
    ...(section.productHandles.length ? { handles: section.productHandles.join(",") } : {}),
  };
  const saleProducts = useListSaleCatalogProducts(saleParams, {
    query: {
      enabled: section.kind === "sale" && storefrontSettings.saleEnabled,
      queryKey: getListSaleCatalogProductsQueryKey(saleParams),
    },
  });
  const settings = storefrontSettings;
  const hasEnabledHero = settings.homepageSections?.some(item => item.enabled && item.kind === "hero") ?? false;
  const eastern = (easternProducts.data?.products ?? []).map(product => product as StoreProduct);
  const selectedBrandProducts = (brandProducts.data?.products ?? []).map(product => ({
    ...product, images: product.images.map(image => ({ ...image })), variants: product.variants.map(variant => ({ ...variant })),
  } as StoreProduct));
  const fullList: StoreProduct[] = section.kind === "sale"
    ? (saleProducts.data?.products ?? []).map(product => product as StoreProduct)
    : hasCatalogBrand ? selectedBrandProducts : eastern;
  const visibleProducts = settings.showSoldOutProductsInLists
    ? fullList
    : fullList.filter(product => product.variants.some(variant => variant.available));
  const homepageSelection = section.productHandles.length
    ? visibleProducts
    : prioritizeHomepageProducts(visibleProducts);
  const list = homepageSelection.slice(0, section.productCount);
  const sectionBrandName = brands.find(brand => brand.slug === section.brandSlug)?.name ?? "";
  const titleFallback = section.kind === "brand-strip"
    ? ""
    : section.kind === "sale"
    ? settings.content.saleHeading
    : section.kind === "hero"
      ? settings.content.heroHeading
      : section.kind === "brand-feature"
        ? sectionBrandName || "Shop by edit"
        : section.kind === "editorial" && isHowItWorksSection(section)
          ? "How it works"
          : section.kind === "editorial"
            ? settings.content.storyHeading
            : settings.content.featuredHeading;
  const eyebrowFallback = section.kind === "brand-strip"
    ? ""
    : section.kind === "sale"
    ? settings.content.saleEyebrow
    : section.kind === "hero"
      ? settings.content.heroEyebrow
      : section.kind === "editorial"
        ? settings.content.storyEyebrow
        : settings.content.featuredEyebrow;
  const subheadingFallback = section.kind === "hero"
    ? settings.content.heroSubheading
    : section.kind === "editorial" && !isHowItWorksSection(section)
      ? settings.content.storyBody
      : "";
  const ctaFallback = section.kind === "brand-strip"
    ? ""
    : section.kind === "hero"
    ? settings.content.heroCtaText
    : section.kind === "sale"
      ? "SHOP ALL SALE"
      : section.kind === "brand-feature"
        ? `SHOP ${sectionBrandName}`
        : "VIEW ALL";
  const eyebrow = resolvedSectionCopy(section, "eyebrow", section.eyebrow, eyebrowFallback, sectionBrandName);
  const title = resolvedSectionCopy(section, "heading", section.heading, titleFallback, sectionBrandName);
  const subheading = resolvedSectionCopy(section, "subheading", section.subheading, subheadingFallback, sectionBrandName);
  const ctaText = resolvedSectionCopy(section, "ctaText", section.ctaText, ctaFallback, sectionBrandName);
  const SectionHeading = isPrimaryHeading ? "h1" : "h2";
  const ctaHref = section.ctaHref || (section.brandSlug ? `/brands/${section.brandSlug}` : section.kind === "sale" ? "/sale" : "/brands/ethnc");
  const image = section.imageUrl;
  if (section.kind === "shop-by-brand") {
    return <HomepageShopByBrand section={section} brands={brands}/>;
  }
  if (section.kind === "hero") return <section className="homepage-hero-section relative mx-auto grid min-h-[560px] max-w-[1440px] grid-cols-1 md:min-h-0 md:grid-cols-[.72fr_1.28fr]" data-testid={`homepage-section-${section.id}`}>
    <div className="homepage-hero-copy flex flex-col justify-center px-7 py-14 md:h-full md:justify-start md:px-6 md:py-6 lg:px-8 xl:px-14">
      <div className="md:my-auto">
       {eyebrow && <p className="mono mb-8 text-[10px] tracking-[.26em] text-[#746e65]">{eyebrow}</p>}
        {title && <SectionHeading className="serif max-w-[550px] whitespace-pre-line text-5xl leading-[.98] tracking-[-.04em] sm:text-7xl xl:text-[86px]">{title}</SectionHeading>}
       {subheading && <p className="mt-7 max-w-[390px] whitespace-pre-line text-sm leading-7 text-[#5f5b55]">{subheading}</p>}
      <div className="mt-8 flex flex-wrap gap-3">
        {activeHeroSlide
          ? <Link href={activeHeroSlide.brandHref} className="solid-button text-[10px] tracking-[.16em]" data-testid={`link-section-shop-${section.id}-${activeHeroSlide.brandSlug}`}>Shop {activeHeroSlide.brandName} <ArrowRight size={15}/></Link>
          : heroShopButtons.length
          ? heroShopButtons.map(brand => <Link key={brand.slug} href={brand.href} className="solid-button text-[10px] tracking-[.16em]" data-testid={`link-section-shop-${section.id}-${brand.slug}`}>Shop {brand.name} <ArrowRight size={15}/></Link>)
           : ctaText ? <Link href={ctaHref} className="solid-button text-[10px] tracking-[.16em]" data-testid={`link-section-cta-${section.id}`}>{ctaText}<ArrowRight size={15}/></Link> : null}
        <Link href="/brands" className="outline-button text-[10px] tracking-[.16em]" data-testid="link-section-all-brands">ALL BRANDS</Link>
      </div>
      <div className="storefront-info-text mt-14 flex items-center gap-3 text-[9px] tracking-[.1em] text-[#5f5b55] md:mt-8"><span className="h-px w-9 bg-[#5f5b55]"/>{settings.content.heroBottomNote}</div>
      </div>
      <HomepageBrandStrip sectionId={`${section.id}-inline`} inline eyebrow="" heading="" subheading="" ctaText=""/>
    </div>
    <HomepageHeroVisual
      rotating={Boolean(activeHeroSlide)}
      slides={heroSlides}
      activeIndex={activeHeroIndex}
      onSelectSlide={onSelectHeroSlide}
      onPauseChange={onHeroPauseChange}
      staticImage={heroLoading ? undefined : image || settings.content.heroImageUrl || fallbackHeroImage?.src}
      staticWidth={image || settings.content.heroImageUrl ? undefined : fallbackHeroImage?.width}
      staticHeight={image || settings.content.heroImageUrl ? undefined : fallbackHeroImage?.height}
      staticEyebrow={section.imageEyebrow || settings.content.heroImageEyebrow}
      staticCaption={section.imageCaption || settings.content.heroImageCaption}
      imageFocalPoints={settings.imageFocalPoints}
      containerClassName="homepage-hero-media"
      gradientClassName="pointer-events-none absolute inset-x-0 bottom-0 h-[38%] bg-gradient-to-t from-black/45 via-black/10 to-transparent"
      captionClassName="absolute bottom-8 left-8 text-white"
    />
  </section>;
  if (section.kind === "brand-strip") {
    return <HomepageBrandStrip
      sectionId={section.id}
      eyebrow={eyebrow}
      heading={title}
      subheading={subheading}
      ctaText={ctaText}
      ctaHref={ctaHref}
      isPrimaryHeading={isPrimaryHeading}
      mobileOnly={hasEnabledHero}
    />;
  }
  if (section.kind === "editorial" && isHowItWorksSection(section)) {
    return <HowItWorksSection section={section} isPrimaryHeading={isPrimaryHeading}/>;
  }
  if (section.kind === "editorial") return <section className={`mx-auto grid max-w-[1440px] items-center gap-9 px-6 py-16 md:px-12 md:py-24 ${section.layout === "featured-visual" && image ? "md:grid-cols-[1.25fr_.75fr]" : section.layout === "asymmetric" ? "md:grid-cols-[.85fr_1.15fr]" : "md:grid-cols-2"} ${section.layout === "banner" ? "bg-[#e7e2d9]" : ""}`} data-testid={`homepage-section-${section.id}`}>
    {image ? <div className="relative min-h-64 overflow-hidden bg-[#e7e2d9] md:min-h-[360px]"><img src={image} alt={section.imageCaption || title} className="absolute inset-0 h-full w-full object-cover"/><div className="absolute bottom-6 left-6 text-white"><p className="mono text-[9px] tracking-[.2em]">{section.imageEyebrow}</p><p className="serif mt-2 text-3xl">{section.imageCaption}</p></div></div> : <div>{eyebrow && <p className="mono text-[10px] tracking-[.2em] text-[#5f5b55]">{eyebrow}</p>}{title && <SectionHeading className="serif mt-4 whitespace-pre-line text-4xl leading-tight md:text-6xl">{title}</SectionHeading>}</div>}
    <div>{image && <>{eyebrow && <p className="mono text-[10px] tracking-[.2em] text-[#5f5b55]">{eyebrow}</p>}{title && <SectionHeading className="serif mt-4 whitespace-pre-line text-4xl leading-tight md:text-6xl">{title}</SectionHeading>}</>}{subheading && <p className="mt-5 max-w-lg whitespace-pre-line text-sm leading-7 text-[#5b5751]">{subheading}</p>}{ctaText && <Link href={ctaHref} className="mt-7 inline-flex items-center gap-3 border-b border-[#171717] pb-2 text-[10px] tracking-[.15em]" data-testid={`link-section-cta-${section.id}`}>{ctaText.toUpperCase()} <ArrowRight size={14}/></Link>}</div>
  </section>;
  if (section.kind === "brand-feature") {
    const brand = brands.find(item => item.slug === section.brandSlug);
    const productsPending = hasCatalogBrand ? brandProducts.isLoading : easternProducts.isLoading;
    const autoLayout = section.layout === "horizontal";
    const layout = autoLayout && brandLayoutBrands.length >= 3 && brandSectionIndex > 0
      ? "grid"
      : autoLayout ? "brand-feature" : section.layout === "banner" ? "brand-feature" : section.layout === "products-first" ? "shop-by-edit" : "grid";
    const featureLayout = ["brand-feature-left", "brand-feature-right"].includes(section.layout) ? "brand-feature" : layout;
    const mirrored = section.layout === "brand-feature-right"
      || (section.layout !== "brand-feature-left" && brandLayoutBrands.length === 2 && brandLayoutBrands.findIndex(item => item.slug === section.brandSlug) === 1);
    if (!brand || brand.productCount <= 0 || (!productsPending && !visibleProducts.length)) return null;
    if (featureLayout === "shop-by-edit") {
      return <BrandShopByEdit section={section} brand={brand} categories={brand.slug === "ethnc" ? ["Pret", ...EASTERN_EDIT] : brand.categories}/>;
    }
    return featureLayout === "brand-feature"
      ? <BrandFeatureSection
          section={section}
          brand={brand}
          products={list}
          description={storefrontSettings.brands.find(item => item.slug === brand.slug)?.description}
          mirrored={mirrored}
          shade={brandSectionIndex % 2 === 1}
          pending={productsPending}
          isPrimaryHeading={isPrimaryHeading}
          ctaHref={ctaHref}
        />
      : <section className={`mx-auto max-w-[1440px] px-5 py-14 md:px-10 md:py-20 ${brandSectionIndex % 2 === 1 ? "bg-[#eee9df]" : "bg-[#f8f5ef]"}`} data-testid={`homepage-section-${section.id}`}>
           <div className="mb-7 flex items-end justify-between gap-4"><div>{eyebrow && <p className="mono text-[9px] tracking-[.2em] text-[#5f5b55]">{eyebrow}</p>}{title && <SectionHeading className="serif mt-3 text-4xl md:text-5xl">{title}</SectionHeading>}{subheading && <p className="mt-3 max-w-xl text-sm leading-6 text-[#68635d]">{subheading}</p>}</div>{ctaText && <Link href={ctaHref} className="hidden items-center gap-2 pb-1 text-[10px] tracking-[.12em] md:flex">{ctaText} <ArrowRight size={14}/></Link>}</div>
          {productsPending ? <ProductSkeletons/> : <div className="grid grid-cols-2 gap-x-3 gap-y-8 md:grid-cols-4 md:gap-x-6">{list.map((product, index) => <ProductCard key={`${brand.slug}-${product.id}`} product={product} index={index} loading="lazy"/>)}</div>}
    </section>;
  }
  const isSale = section.kind === "sale";
  const pending = hasCatalogBrand ? brandProducts.isLoading : isSale ? saleProducts.isLoading : easternProducts.isLoading;
  if (isSale && !settings.saleEnabled) return null;
  if ((hasCatalogBrand || section.kind === "product-collection" || isSale) && !pending && !list.length) return null;
  if (isSale && !pending && visibleProducts.length < 4) return null;
  if (isSale) return <SaleBand section={section} products={visibleProducts.slice(0, Math.max(4, section.productCount))} pending={pending} isPrimaryHeading={isPrimaryHeading}/>;
  return <section className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24" data-testid={`homepage-section-${section.id}`}>
      <div className="mb-8 flex items-end justify-between gap-4"><div>{eyebrow && <p className="mono text-[9px] tracking-[.2em] text-[#5f5b55]">{eyebrow}</p>}{title && <SectionHeading className="serif mt-3 text-4xl md:text-5xl">{title}</SectionHeading>}{subheading && <p className="mt-3 max-w-xl text-sm leading-6 text-[#68635d]">{subheading}</p>}</div>{ctaText && <Link href={ctaHref} className="hidden items-center gap-2 pb-1 text-[10px] tracking-[.12em] md:flex" data-testid={`link-section-cta-${section.id}`}>{ctaText} <ArrowRight size={14}/></Link>}</div>
    {pending ? <ProductSkeletons/> : list.length ? <div className={`grid grid-cols-2 gap-x-3 gap-y-8 md:gap-x-6 ${section.layout === "horizontal" ? "md:grid-cols-3" : section.layout === "grid" || section.layout === "products-first" ? "md:grid-cols-4" : "md:grid-cols-4"}`}>{list.map((product, index) => <ProductCard key={`${product.brandSlug ?? "ethnc"}-${product.id}`} product={product} index={index} loading="lazy"/>)}</div> : null}
     {ctaText && <Link href={ctaHref} className="outline-button mt-8 w-full text-[10px] tracking-[.15em] md:hidden" data-testid={`link-section-mobile-cta-${section.id}`}>{ctaText.toUpperCase()} <ArrowRight size={14}/></Link>}
   </section>;
});